import { splitLabelNumber } from '../model/labels.js';
import { IN_TRAY, areaOrigin, clampAreaSize, mainStage, worldPosition } from '../model/areas.js';
import { makeItem } from '../model/board.js';
import { instrumentShape } from '../model/symbols.js';
import { newId } from '../model/ids.js';
import type {
  BoardData,
  CatalogEntry,
  Channel,
  ConnectorType,
  FreeformElement,
  Frame,
  Item,
  ItemKind,
  Mix,
  PowerSource,
} from '../model/schema.js';
import { itemFromCatalog, matchCatalogToken } from '../catalog/catalog.js';
import { matchSourceText, parseStandText, sourceTokens } from '../stands/fromChannels.js';
import { normalizeStandText } from '../stands/stands.js';
import type { AiExtraction, AiMix, AiPosition } from './schema.js';
import type {
  AiConflict,
  AiMatch,
  AiMergeOp,
  AiMergeOptions,
  AiMergePlan,
  AiOpGroup,
} from './types.js';

/**
 * Deterministic merge of an AI extraction into a board (decision U9). The plan only proposes
 * ops; nothing on the board changes until {@link applyAiMerge}. Matching is content based, so
 * planning the same extraction against the board it was applied to proposes nothing new.
 */

/** Lower-case letters and digits only, accents folded ("Flügel 1" → "flugel1"). */
export function normalizeName(text: string): string {
  return (
    text
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      // Stryker disable next-line Regex: equivalent – removing runs or single characters gives the same string.
      .replace(/[^\p{L}\p{N}]+/gu, '')
  );
}

/** Words that describe the device rather than the source ("Moderation Mic" ≈ "Moderation"). */
const DEVICE_WORDS = /\b(?:mic|mics|mikro|mikrofon|mikrofone|microphone|di|box|input)\b/giu;

/** Loose name equality: equal after removing device words, or one contains the other (≥ 3 chars). */
export function similarName(a: string, b: string): boolean {
  const x = normalizeName(a.replace(DEVICE_WORDS, ' '));
  const y = normalizeName(b.replace(DEVICE_WORDS, ' '));
  if (x === '' || y === '') return false;
  if (x === y) return true;
  // Stryker disable next-line EqualityOperator: equivalent – for equal lengths containment is symmetric.
  const [short, long] = x.length <= y.length ? [x, y] : [y, x];
  return short.length >= 3 && long.includes(short);
}

const FT = 0.3048;
/** Distance (m) within which an unassigned mic/monitor is attributed to the nearest musician. */
const NEAR_MUSICIAN = 1.5;
/** A placed source this close to an entry's position (m) is "the one standing there". */
const SAME_SPOT = 0.01;
/** Placed items keep their centre at least this far inside the frame edges. */
const EDGE_MARGIN = 0.2;
const NOTE_W = 2.4;
const NOTE_H = 1.2;
const NOTE_GAP = 0.3;

interface Placement {
  frameId: string | null;
  x: number;
  y: number;
  unplaced?: true;
}

const UNPLACED: Placement = { ...IN_TRAY, x: 0, y: 0 };

/** The item at a real position: it leaves the tray, so `unplaced` is dropped. */
function placedItem(item: Item, placement: Placement): Item {
  const placed: Item = { ...item, ...placement };
  delete placed.unplaced;
  return placed;
}

/** Centimetre precision keeps proposals readable (2.4, not 2.4000000000000004). */
function round(v: number): number {
  return Math.round(v * 100) / 100;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** Frame-local point clamped so the centre lies inside the frame. */
export function clampIntoFrame(frame: Frame, x: number, y: number): { x: number; y: number } {
  const mx = Math.min(EDGE_MARGIN, frame.width / 2);
  const my = Math.min(EDGE_MARGIN, frame.depth / 2);
  return { x: round(clamp(x, mx, frame.width - mx)), y: round(clamp(y, my, frame.depth - my)) };
}

/** Small deterministic spread for the n-th object placed near the same anchor (downstage of it). */
function spread(n: number): { dx: number; dy: number } {
  return { dx: ((n % 3) - 1) * 0.45, dy: 0.5 + Math.floor(n / 3) * 0.35 };
}

interface ChannelState {
  key: string;
  entity: Channel;
  isNew: boolean;
  original: Channel | undefined;
  /** Proposed values that differ from non-empty existing ones. */
  overwrite: Partial<Channel>;
}

interface Ctx {
  board: BoardData;
  catalog: readonly CatalogEntry[];
  nextId: () => string;
  frame: Frame | undefined;
  ops: AiMergeOp[];
  matches: AiMatch[];
  conflicts: AiConflict[];
  warnings: string[];
  /** Existing + planned items. */
  items: Map<string, Item>;
  /** Existing items already matched to an extraction entry (count matching). */
  claimed: Set<string>;
  musicians: Item[];
  channels: ChannelState[];
  /** Extraction channel number → channel it landed on (matched by name it may differ). */
  byExtNumber: Map<number, ChannelState>;
  /** Channel ids that got a source in this plan or matched one. */
  sourced: Set<string>;
  keys: Map<string, number>;
  /** Objects already placed near an anchor item (spread index). */
  nearCount: Map<string, number>;
}

/** Unique op key: the second "wedge:wedge" becomes "wedge:wedge#2". */
function uniqueKey(ctx: Ctx, base: string): string {
  const n = (ctx.keys.get(base) ?? 0) + 1;
  ctx.keys.set(base, n);
  return n === 1 ? base : `${base}#${n}`;
}

function place(ctx: Ctx, pos: AiPosition | undefined): Placement {
  if (pos === undefined || ctx.frame === undefined) return UNPLACED;
  const f = ctx.frame;
  return {
    frameId: f.id,
    ...clampIntoFrame(f, clamp(pos.x, 0, 1) * f.width, clamp(pos.y, 0, 1) * f.depth),
  };
}

/** Placement near an anchor item (n-th object there), else unplaced. */
function near(ctx: Ctx, anchor: Item, n: number): Placement {
  const at = worldPosition(ctx.board, anchor);
  if (at === undefined || ctx.frame === undefined) return UNPLACED;
  const { dx, dy } = spread(n);
  const f = ctx.frame;
  return { frameId: f.id, ...clampIntoFrame(f, at.x - f.x + dx, at.y - f.y + dy) };
}

function placeFor(ctx: Ctx, pos: AiPosition | undefined, anchor: Item | undefined): Placement {
  if (pos !== undefined) return place(ctx, pos);
  if (anchor === undefined) return UNPLACED;
  const n = ctx.nearCount.get(anchor.id) ?? 0;
  ctx.nearCount.set(anchor.id, n + 1);
  return near(ctx, anchor, n);
}

function findMusician(ctx: Ctx, name: string | undefined): Item | undefined {
  if (name === undefined) return undefined;
  const n = normalizeName(name);
  return (
    ctx.musicians.find((m) => normalizeName(m.label) === n) ??
    ctx.musicians.find((m) => similarName(m.label, name))
  );
}

function nearestMusician(ctx: Ctx, p: Placement): Item | undefined {
  const o = p.unplaced === true ? undefined : areaOrigin(ctx.board, p);
  if (o === undefined) return undefined;
  let best: Item | undefined;
  let bestD = NEAR_MUSICIAN;
  for (const m of ctx.musicians) {
    const w = worldPosition(ctx.board, m);
    if (w === undefined) continue;
    const d = Math.hypot(w.x - (o.x + p.x), w.y - (o.y + p.y));
    if (d <= bestD) {
      best = m;
      bestD = d;
    }
  }
  return best;
}

function createItem(
  ctx: Ctx,
  key: string,
  group: AiOpGroup,
  item: Item,
  label: string,
  detail?: string,
): Item {
  ctx.ops.push({
    key,
    group,
    kind: 'create',
    collection: 'items',
    entity: item,
    label,
    ...(detail ? { detail } : {}),
    defaultSelected: true,
  });
  ctx.items.set(item.id, item);
  return item;
}

/** An existing, not yet claimed item of `kind` with the same label (count matching). */
function claimExisting(
  ctx: Ctx,
  kinds: readonly ItemKind[],
  label: string,
  prefer: readonly string[] = [],
) {
  const n = normalizeName(label);
  // unique labels (U34) number namesakes "SM58 (1)", "SM58 (2)": a re-imported "SM58" still
  // finds them – exact names first, then by number, else in board order (stable sort, OIL-62)
  const base = (i: Item) => normalizeName(splitLabelNumber(i.label).base);
  const rank = (i: Item) => (normalizeName(i.label) === n ? 0 : 1);
  const candidates = Object.values(ctx.board.items)
    .filter(
      (i) =>
        kinds.includes(i.kind) &&
        !ctx.claimed.has(i.id) &&
        (normalizeName(i.label) === n || base(i) === n),
    )
    .sort(
      (a, b) =>
        rank(a) - rank(b) ||
        (splitLabelNumber(a.label).n ?? 0) - (splitLabelNumber(b.label).n ?? 0),
    );
  const hit = candidates.find((i) => prefer.includes(i.id)) ?? candidates[0];
  if (hit !== undefined) ctx.claimed.add(hit.id);
  return hit;
}

/** Drum kit wording on boards and riders ("Drummer", "Drumset", "Schlagzeug" …). */
const DRUM_KIT = /^(drums?|drummer|drum ?(set|kit)|schlagzeug|schlagwerk)$/iu;

/**
 * Delta import: an unclaimed item named similarly ("Piano" ↔ "Grand Piano", "Drummer" ↔
 * "Drumset"), used when no exact name matches.
 */
function claimSimilar(ctx: Ctx, kinds: readonly ItemKind[], label: string): Item | undefined {
  const drums = DRUM_KIT.test(label.trim());
  const hit = Object.values(ctx.board.items).find(
    (i) =>
      kinds.includes(i.kind) &&
      !ctx.claimed.has(i.id) &&
      (similarName(i.label, label) || (drums && DRUM_KIT.test(i.label.trim()))),
  );
  if (hit !== undefined) ctx.claimed.add(hit.id);
  return hit;
}

/**
 * Delta import: an existing mic/DI that no channel uses yet, named like the channel or of one of
 * the given models ("RE20, MD421"), so the channel is linked instead of getting a new source.
 */
function claimUnlinkedSource(
  ctx: Ctx,
  kinds: readonly ItemKind[],
  models: string,
  name: string,
): Item | undefined {
  const linked = new Set(Object.values(ctx.board.channels).map((c) => c.sourceItemId));
  const tokens = new Set(sourceTokens(models).map(normalizeName));
  const free = Object.values(ctx.board.items).filter(
    (i) => kinds.includes(i.kind) && !ctx.claimed.has(i.id) && !linked.has(i.id),
  );
  const n = normalizeName(name);
  const hit =
    free.find((i) => n !== '' && normalizeName(i.label) === n) ??
    free.find(
      (i) =>
        tokens.has(normalizeName(i.model ?? '')) ||
        tokens.has(normalizeName(`${i.brand ?? ''} ${i.model ?? ''}`)),
    );
  if (hit !== undefined) ctx.claimed.add(hit.id);
  return hit;
}

function catalogItem(
  ctx: Ctx,
  kinds: readonly ItemKind[],
  model: string | undefined,
  fallback: ItemKind,
  base: Partial<Item>,
): Item {
  const entries = ctx.catalog.filter((e) => kinds.includes(e.kind));
  const entry = model === undefined ? undefined : matchSourceText(entries, model);
  if (entry === undefined) {
    return makeItem(fallback, { ...base, ...(model !== undefined ? { model } : {}) });
  }
  const item = itemFromCatalog(entry, base);
  if (sourceTokens(model!).length > 1) item.notes = model;
  return item;
}

/** "a · b" of the trimmed, non-empty parts. */
function describe(...parts: (string | undefined)[]): string {
  return parts
    .map((p) => p?.trim() ?? '')
    .filter((p) => p !== '')
    .join(' · ');
}

/** `{ detail }` when there is one (review rows without detail stay compact). */
function detailOf(...parts: (string | undefined)[]): { detail?: string } {
  const detail = describe(...parts);
  return detail === '' ? {} : { detail };
}

/**
 * Records a match with an existing item. When the item still waits in the unplaced tray and the
 * document shows where it stands, a (pre-selected) patch op places it – together with its stand.
 */
function matchExisting(
  ctx: Ctx,
  kind: AiMatch['kind'],
  key: string,
  item: Item,
  group: AiOpGroup,
  pos: AiPosition | undefined,
): void {
  ctx.matches.push({ kind, key, entityId: item.id, label: item.label });
  const placement = place(ctx, pos);
  if (item.unplaced !== true || placement.unplaced === true) return;
  const stand = item.mountedOn === undefined ? undefined : ctx.board.items[item.mountedOn];
  if (stand !== undefined && stand.unplaced === true) {
    ctx.ops.push({
      key: `${key}:place-stand`,
      group: 'stands',
      kind: 'patch',
      collection: 'items',
      entity: placedItem(stand, placement),
      label: stand.label,
      detail: item.label,
      defaultSelected: true,
    });
  }
  const placed = placedItem(item, placement);
  ctx.items.set(item.id, placed);
  ctx.ops.push({
    key: `${key}:place`,
    group,
    kind: 'patch',
    collection: 'items',
    entity: placed,
    label: item.label,
    defaultSelected: true,
  });
}

// ---------------------------------------------------------------------------------------------
// Stage

function planStage(ctx: Ctx, extraction: AiExtraction): void {
  const frame = ctx.frame;
  const stage = extraction.stage;
  if (frame === undefined || stage === undefined) return;
  const f = stage.units === 'ft' ? FT : 1;
  const want = (v: number | undefined, current: number): number =>
    v === undefined ? current : Number((v * f).toFixed(2));
  // Areas never overlap: the stage grows only up to its neighbours.
  const { width, depth } = clampAreaSize(
    Object.values(ctx.board.frames),
    frame.id,
    want(stage.width, frame.width),
    want(stage.depth, frame.depth),
  );
  if (Math.abs(width - frame.width) < 0.01 && Math.abs(depth - frame.depth) < 0.01) return;
  const busy = Object.values(ctx.board.items).some((i) => i.frameId === frame.id);
  const next: Frame = { ...frame, width, depth };
  ctx.ops.push({
    key: 'stage',
    group: 'stage',
    kind: 'patch',
    collection: 'frames',
    entity: next,
    label: `${next.width} × ${next.depth} m`,
    detail: `${frame.width} × ${frame.depth} m`,
    defaultSelected: !busy,
  });
  if (!busy) ctx.frame = next;
}

// ---------------------------------------------------------------------------------------------
// Musicians

function planMusicians(ctx: Ctx, extraction: AiExtraction): void {
  for (const m of extraction.musicians) {
    const name = m.name.trim();
    const key = `musician:${normalizeName(name)}`;
    if (name === '' || ctx.keys.has(key)) continue;
    ctx.keys.set(key, 1);
    const existing = ctx.musicians.find((x) => normalizeName(x.label) === normalizeName(name));
    if (existing !== undefined) {
      ctx.claimed.add(existing.id);
      matchExisting(ctx, 'musician', key, existing, 'musicians', m.position);
      continue;
    }
    // Delta import: players are often drawn as instrument objects ("Trp1", "Sax 2") – reuse
    // them, unless the rider also lists an instrument of that name (then it is the instrument).
    const listedAsInstrument = extraction.instruments.some(
      (i) => normalizeName(i.name) === normalizeName(name),
    );
    const drawn = listedAsInstrument ? undefined : claimExisting(ctx, ['instrument'], name);
    if (drawn !== undefined) {
      matchExisting(ctx, 'musician', key, drawn, 'musicians', m.position);
      ctx.musicians.push(drawn);
      continue;
    }
    const item = makeItem('musician', {
      id: ctx.nextId(),
      label: name,
      ...place(ctx, m.position),
      ...(m.instrument ? { notes: m.instrument } : {}),
    });
    ctx.musicians.push(createItem(ctx, key, 'musicians', item, name, m.instrument));
  }
}

// ---------------------------------------------------------------------------------------------
// Channels

function channelLabel(ch: Pick<Channel, 'number' | 'name'>): string {
  return `Ch ${ch.number} ${ch.name}`.trim();
}

type ChannelField = 'name' | 'sourceText' | 'stand' | 'insert' | 'patch' | 'notes' | 'group';

/** Fills empty fields of a channel; differing non-empty values become overwrite proposals. */
function fillChannel(state: ChannelState, field: ChannelField, value: string | undefined): void {
  const v = value?.trim();
  if (v === undefined || v === '') return;
  const cur = state.entity[field];
  if (cur === undefined || cur.trim() === '') {
    state.entity[field] = v;
  } else if (normalizeName(cur) !== normalizeName(v) && !state.isNew) {
    state.overwrite[field] = v;
  }
}

function fillPhantom(state: ChannelState, value: boolean | undefined): void {
  if (value === undefined) return;
  if (state.entity.phantom === undefined) state.entity.phantom = value;
  else if (state.entity.phantom !== value && !state.isNew) state.overwrite.phantom = value;
}

function planChannels(ctx: Ctx, extraction: AiExtraction): void {
  const existing = Object.values(ctx.board.channels).sort((a, b) => a.number - b.number);
  for (const ch of existing) {
    ctx.channels.push({
      key: `channel:${ch.number}`,
      entity: { ...ch },
      isNew: false,
      original: ch,
      overwrite: {},
    });
  }
  const seen = new Set<number>();
  const extNumbers = new Set(extraction.channels.map((c) => c.number));
  const matched = new Set<string>();
  for (const c of [...extraction.channels].sort((a, b) => a.number - b.number)) {
    if (seen.has(c.number)) {
      ctx.warnings.push(`Duplicate channel number ${c.number} (“${c.name}”) ignored`);
      continue;
    }
    seen.add(c.number);
    const key = `channel:${c.number}`;
    const name = c.name.trim();
    let state =
      ctx.channels.find((s) => !s.isNew && s.entity.number === c.number) ??
      ctx.channels.find(
        (s) =>
          !s.isNew &&
          !matched.has(s.entity.id) &&
          !extNumbers.has(s.entity.number) &&
          name !== '' &&
          normalizeName(s.entity.name) === normalizeName(name),
      );
    if (state === undefined) {
      state = {
        key,
        entity: { id: ctx.nextId(), number: c.number, name },
        isNew: true,
        original: undefined,
        overwrite: {},
      };
      ctx.channels.push(state);
    } else {
      ctx.matches.push({
        kind: 'channel',
        key,
        entityId: state.entity.id,
        label: channelLabel(state.entity),
      });
    }
    matched.add(state.entity.id);
    ctx.byExtNumber.set(c.number, state);
    fillChannel(state, 'name', name);
    if (!hasSource(ctx, state.entity)) fillChannel(state, 'sourceText', c.source ?? c.micModel);
    fillChannel(state, 'stand', c.stand);
    fillChannel(state, 'insert', c.insert);
    fillChannel(state, 'patch', c.patch);
    fillChannel(state, 'notes', c.notes);
    fillChannel(state, 'group', findMusician(ctx, c.musician)?.label ?? c.musician);
    fillPhantom(state, c.phantom);
  }
}

function hasSource(ctx: Ctx, ch: Channel): boolean {
  return ch.sourceItemId !== undefined && ctx.items.has(ch.sourceItemId);
}

function channelByNumber(ctx: Ctx, n: number | undefined): ChannelState | undefined {
  if (n === undefined) return undefined;
  return ctx.byExtNumber.get(n) ?? ctx.channels.find((s) => s.entity.number === n);
}

/**
 * Channel whose name resembles `purpose` and that no other source of this plan took. Channels
 * with a source (placed ones, then those of the same kind first) rank before free ones, so a re-import finds
 * the item it linked before; ties go to the lowest number.
 */
function channelByName(
  ctx: Ctx,
  purpose: string,
  kind: 'microphone' | 'di',
  pos?: AiPosition,
): ChannelState | undefined {
  const target = place(ctx, pos);
  const score = (s: ChannelState): number => {
    const src = hasSource(ctx, s.entity) ? ctx.items.get(s.entity.sourceItemId!) : undefined;
    if (src === undefined) return 0;
    const sw = worldPosition(ctx.board, src);
    const o = target.unplaced === true ? undefined : areaOrigin(ctx.board, target);
    const at =
      sw !== undefined &&
      o !== undefined &&
      Math.hypot(sw.x - (o.x + target.x), sw.y - (o.y + target.y)) < SAME_SPOT;
    return 4 + (at ? 2 : 0) + (src.kind === kind ? 1 : 0);
  };
  const free = ctx.channels
    .filter((s) => !ctx.sourced.has(s.entity.id))
    .sort((a, b) => score(b) - score(a) || a.entity.number - b.entity.number);
  return (
    free.find((s) => normalizeName(s.entity.name) === normalizeName(purpose)) ??
    free.find((s) => similarName(s.entity.name, purpose))
  );
}

function emitChannelOps(ctx: Ctx): void {
  for (const s of ctx.channels) {
    const label = channelLabel(s.entity);
    if (s.isNew) {
      ctx.ops.push({
        key: s.key,
        group: 'channels-new',
        kind: 'create',
        collection: 'channels',
        entity: s.entity,
        label,
        ...detailOf(s.entity.sourceText, s.entity.stand),
        defaultSelected: true,
      });
      continue;
    }
    const original = s.original!;
    const changed = Object.keys(s.entity).some(
      (k) => s.entity[k as keyof Channel] !== original[k as keyof Channel],
    );
    if (changed) {
      ctx.ops.push({
        key: s.key,
        group: 'channels-linked',
        kind: 'patch',
        collection: 'channels',
        entity: s.entity,
        label,
        ...detailOf(s.entity.sourceText, s.entity.stand),
        defaultSelected: true,
      });
    }
    const fields = Object.keys(s.overwrite) as (keyof Channel)[];
    if (fields.length === 0) continue;
    const opKey = `${s.key}:overwrite`;
    for (const field of fields) {
      ctx.conflicts.push({
        key: s.key,
        entityId: s.entity.id,
        label,
        field,
        existing: String(s.entity[field]),
        proposed: String(s.overwrite[field]),
        opKey,
      });
    }
    ctx.ops.push({
      key: opKey,
      group: 'conflicts',
      kind: 'patch',
      collection: 'channels',
      entity: { ...s.entity, ...s.overwrite },
      label,
      detail: fields.map((f) => `${f}: ${String(s.overwrite[f])}`).join(' · '),
      defaultSelected: false,
    });
  }
}

// ---------------------------------------------------------------------------------------------
// Sources (microphones, DIs) and their stands

interface SourceEntry {
  kind: 'microphone' | 'di';
  purpose: string;
  model: string | undefined;
  channel: number | undefined;
  stand: string | undefined;
  musician: string | undefined;
  phantom: boolean | undefined;
  position: AiPosition | undefined;
  /** Derived from a channel row (no explicit mic entry). */
  fromChannel: boolean;
}

const DI_TEXT = /(?<![\p{L}\p{N}])di(?![\p{L}])/iu;

function sourceEntries(extraction: AiExtraction): SourceEntry[] {
  const out: SourceEntry[] = [];
  for (const m of extraction.microphones) {
    const n = m.channel === undefined ? (m.quantity ?? 1) : 1;
    for (let i = 0; i < n; i++) {
      out.push({
        kind: 'microphone',
        purpose: m.purpose,
        model: m.model,
        channel: m.channel,
        stand: m.stand,
        musician: m.musician,
        phantom: m.phantom,
        position: m.position,
        fromChannel: false,
      });
    }
  }
  for (const d of extraction.dis) {
    out.push({
      kind: 'di',
      purpose: d.purpose,
      model: d.model,
      channel: d.channel,
      stand: undefined,
      musician: d.musician,
      phantom: undefined,
      position: d.position,
      fromChannel: false,
    });
  }
  const covered = new Set(out.map((s) => s.channel));
  for (const c of extraction.channels) {
    const text = (c.micModel ?? c.source)?.trim();
    if (covered.has(c.number) || text === undefined || text === '') continue;
    covered.add(c.number);
    out.push({
      kind: DI_TEXT.test(text) ? 'di' : 'microphone',
      purpose: c.name,
      model: text,
      channel: c.number,
      stand: c.stand,
      musician: c.musician,
      phantom: c.phantom,
      position: undefined,
      fromChannel: true,
    });
  }
  return out;
}

/**
 * Hidden prop (system key, see `isSystemProp`) recording which extraction entry created a
 * mic/DI, so a re-import finds it even when several channels share a name.
 */
export const AI_ORIGIN_PROP = 'ai:origin';

function planSources(ctx: Ctx, extraction: AiExtraction): void {
  const entries = sourceEntries(extraction).map((src) => {
    const purpose = src.purpose.trim() || src.model?.trim() || src.kind;
    const origin = uniqueKey(
      ctx,
      src.fromChannel ? `origin:ch${src.channel}` : `origin:${src.kind}:${normalizeName(purpose)}`,
    );
    return { src, purpose, origin };
  });
  // Entries with a position first: they take the sources standing there, so a re-import does not
  // hand those to an entry without one (and then place another source).
  entries.sort(
    (a, b) => Number(b.src.position !== undefined) - Number(a.src.position !== undefined),
  );
  // Items an earlier import created for an entry (and their channels) are reserved for their own entry first, so no other entry (matched
  // by channel name) takes them over.
  const known = new Map<string, Item>();
  for (const { origin } of entries) {
    const hit = Object.values(ctx.board.items).find(
      (i) => i.props[AI_ORIGIN_PROP] === origin && !ctx.claimed.has(i.id),
    );
    if (hit === undefined) continue;
    ctx.claimed.add(hit.id);
    known.set(origin, hit);
    const linked = ctx.channels.find((s) => s.entity.sourceItemId === hit.id);
    if (linked !== undefined) ctx.sourced.add(linked.entity.id);
  }
  for (const { src, purpose, origin } of entries) {
    const hit = known.get(origin);
    if (hit !== undefined) {
      const group = src.kind === 'di' ? 'dis' : 'mics';
      matchExisting(ctx, src.kind, origin.replace(/^origin:/, 'src:'), hit, group, src.position);
      continue;
    }
    const state =
      channelByNumber(ctx, src.channel) ??
      (src.channel === undefined ? channelByName(ctx, purpose, src.kind, src.position) : undefined);
    const prefix = src.kind === 'di' ? 'di' : 'mic';
    const key = uniqueKey(
      ctx,
      state === undefined
        ? `${prefix}:${normalizeName(purpose)}`
        : `${prefix}:ch${state.entity.number}`,
    );
    if (state !== undefined && hasSource(ctx, state.entity)) {
      const first = !ctx.sourced.has(state.entity.id);
      ctx.sourced.add(state.entity.id);
      const item = ctx.items.get(state.entity.sourceItemId!)!;
      ctx.claimed.add(item.id);
      if (first && ctx.board.items[item.id] !== undefined) {
        matchExisting(ctx, src.kind, key, item, src.kind === 'di' ? 'dis' : 'mics', src.position);
      }
      continue;
    }
    const sourceKinds: ItemKind[] = src.kind === 'di' ? ['di'] : ['microphone', 'di'];
    if (state !== undefined) {
      const hit = claimUnlinkedSource(
        ctx,
        sourceKinds,
        [src.model, state.entity.sourceText].filter((t) => t !== undefined).join(', '),
        state.entity.name,
      );
      if (hit !== undefined) {
        ctx.sourced.add(state.entity.id);
        matchExisting(ctx, src.kind, key, hit, src.kind === 'di' ? 'dis' : 'mics', src.position);
        state.entity.sourceItemId = hit.id;
        if (hit.ports?.some((p) => p.id === 'out')) state.entity.sourcePortId = 'out';
        else delete state.entity.sourcePortId;
        fillPhantom(state, src.phantom);
        continue;
      }
    }
    if (state === undefined) {
      const hit = claimExisting(ctx, ['microphone', 'di'], purpose);
      if (hit !== undefined) {
        matchExisting(ctx, src.kind, key, hit, src.kind === 'di' ? 'dis' : 'mics', src.position);
        continue;
      }
    }
    const placement0 = place(ctx, src.position);
    // A named but unknown musician is kept as text; only unnamed mics go to the nearest one.
    const named = src.musician ?? state?.entity.group;
    const musician =
      findMusician(ctx, named) ??
      (named === undefined ? nearestMusician(ctx, placement0) : undefined);
    const placement = placeFor(ctx, src.position, musician);
    const model = src.model?.trim() || state?.entity.sourceText;
    const item = catalogItem(ctx, sourceKinds, model || undefined, src.kind, {
      id: ctx.nextId(),
      label: state?.entity.name.trim() || purpose,
      ...placement,
      ...(musician !== undefined || named !== undefined ? { group: musician?.label ?? named } : {}),
    });
    item.props = { ...item.props, [AI_ORIGIN_PROP]: origin };
    const standText = normalizeStandText(src.stand ?? state?.entity.stand ?? '');
    const label = state === undefined ? item.label : channelLabel(state.entity);
    if (standText !== '') {
      const spec = parseStandText(standText);
      const stand = makeItem('stand', {
        id: ctx.nextId(),
        label: standText,
        frameId: item.frameId,
        ...(item.unplaced === true ? { unplaced: true as const } : {}),
        x: item.x,
        y: item.y,
        ...(spec !== null ? { stand: spec } : {}),
        ...(item.group !== undefined ? { group: item.group } : {}),
      });
      item.mountedOn = stand.id;
      createItem(ctx, `${key}:stand`, 'stands', stand, standText, label);
    }
    createItem(
      ctx,
      key,
      src.kind === 'di' ? 'dis' : 'mics',
      item,
      label,
      describe(model, standText),
    );
    if (state !== undefined) {
      ctx.sourced.add(state.entity.id);
      state.entity.sourceItemId = item.id;
      if (item.ports?.some((p) => p.id === 'out')) state.entity.sourcePortId = 'out';
      else delete state.entity.sourcePortId;
      fillPhantom(state, src.phantom);
      // never a conflict: the attribution is a guess, the channel's own group wins
      if (musician !== undefined && state.entity.group === undefined) {
        state.entity.group = musician.label;
      }
    }
  }
}

// ---------------------------------------------------------------------------------------------
// Instruments, monitors, stage boxes, risers

/** Stage furniture models still list as instruments ("5 chairs", "Notenpult") – never backline. */
export const FURNITURE =
  /\b(chairs?|stools?|stuhl|st[üu]hle|hocker|music ?stands?|notenpult\w*|notenst[äa]nder|podium|podest|dirigentenpodest|pult ?lights?|stand ?lights?|pultleuchte\w*)\b/iu;

/** Instruments the model listed that are furniture: kept as notes ("5× Chairs without armrests"). */
export function splitFurniture(extraction: AiExtraction): AiExtraction {
  const furniture = extraction.instruments.filter((i) => FURNITURE.test(i.name));
  if (furniture.length === 0) return extraction;
  const notes = furniture.map((i) => {
    const text = `${i.quantity ?? 1}× ${i.name.trim()}`;
    return i.musician ? `${text} (${i.musician})` : text;
  });
  return {
    ...extraction,
    instruments: extraction.instruments.filter((i) => !FURNITURE.test(i.name)),
    notes: [...extraction.notes, ...notes],
  };
}

function planInstruments(ctx: Ctx, extraction: AiExtraction): void {
  for (const ins of extraction.instruments) {
    const label = ins.name.trim();
    if (label === '') continue;
    const kind: ItemKind = /amp|combo|verst[äa]rker|\bcab/iu.test(label) ? 'amp' : 'instrument';
    for (let i = 0; i < (ins.quantity ?? 1); i++) {
      const key = uniqueKey(ctx, `instrument:${normalizeName(label)}`);
      const hit = claimExisting(ctx, [kind], label) ?? claimSimilar(ctx, [kind], label);
      if (hit !== undefined) {
        matchExisting(ctx, 'item', key, hit, 'instruments', ins.position);
        continue;
      }
      const musician = findMusician(ctx, ins.musician);
      const item = catalogItem(ctx, [kind], ins.model, kind, {
        id: ctx.nextId(),
        label,
        ...placeFor(ctx, ins.position, musician),
        // mid-size grand footprint, like the library's "Grand piano"
        ...(kind === 'instrument' &&
        instrumentShape({ label, ...(ins.model ? { model: ins.model } : {}) })
          ? { width: 1.5, depth: 2.1, props: { subtype: 'grand_piano' } }
          : {}),
        ...(musician !== undefined ? { group: musician.label } : {}),
        ...(ins.notes ? { notes: ins.notes } : {}),
      });
      createItem(ctx, key, 'instruments', item, label, describe(ins.model, ins.musician));
    }
  }
}

const MONITOR_LABEL = { wedge: 'Wedge', sidefill: 'Sidefill', iem: 'IEM', drumfill: 'Drumfill' };

interface MixTarget {
  key: string;
  mix: Mix;
  isNew: boolean;
  original: Mix | undefined;
}

interface MixPlan {
  targets: MixTarget[];
  /** Target per entry of `extraction.mixes` (same index). */
  ext: MixTarget[];
}

/** Mix a monitor/send reference ("Drums", "3") points to: by name, else by number. */
function mixFor(targets: readonly MixTarget[], ref: string): MixTarget | undefined {
  const n = normalizeName(ref);
  return (
    targets.find((t) => normalizeName(t.mix.name) === n) ??
    targets.find((t) => String(t.mix.number) === ref.trim())
  );
}

function planMixTargets(ctx: Ctx, extraction: AiExtraction): MixPlan {
  const targets: MixTarget[] = Object.values(ctx.board.mixes)
    .sort((a, b) => a.number - b.number)
    .map((m) => ({
      key: `mix:${m.number}`,
      mix: { ...m, sends: [...m.sends], outputItemIds: [...m.outputItemIds] },
      isNew: false,
      original: m,
    }));
  const claimed = new Set<MixTarget>();
  const byName = new Map<string, MixTarget>();
  const resolve = (w: AiMix): MixTarget => {
    const name = w.name.trim();
    const hit =
      byName.get(normalizeName(name)) ??
      targets.find((t) => normalizeName(t.mix.name) === normalizeName(name)) ??
      targets.find((t) => !claimed.has(t) && w.number !== undefined && t.mix.number === w.number);
    if (hit !== undefined) {
      if (!hit.isNew && !claimed.has(hit)) {
        ctx.matches.push({ kind: 'mix', key: hit.key, entityId: hit.mix.id, label: hit.mix.name });
      }
      claimed.add(hit);
      byName.set(normalizeName(name), hit);
      return hit;
    }
    const used = new Set(targets.map((t) => t.mix.number));
    let number = w.number ?? 1;
    while (used.has(number)) number += 1;
    const iem = w.kind === 'iem' || /iem|in.?ear/iu.test(name);
    const target: MixTarget = {
      key: `mix:${number}`,
      mix: {
        id: ctx.nextId(),
        number,
        name,
        kind: w.kind ?? (iem ? 'iem' : 'monitor'),
        stereo: w.stereo ?? iem,
        sends: [],
        outputItemIds: [],
      },
      isNew: true,
      original: undefined,
    };
    targets.push(target);
    claimed.add(target);
    byName.set(normalizeName(name), target);
    return target;
  };
  const ext = extraction.mixes.map(resolve);
  for (const m of extraction.monitors) {
    if (m.mix === undefined || mixFor(targets, m.mix) !== undefined) continue;
    resolve({ name: m.mix, ...(/^\d+$/.test(m.mix.trim()) ? { number: Number(m.mix) } : {}) });
  }
  return { targets, ext };
}

function planMonitors(ctx: Ctx, extraction: AiExtraction, mixes: MixPlan): void {
  for (const mon of extraction.monitors) {
    const kind: ItemKind = mon.type === 'drumfill' ? 'wedge' : mon.type;
    const who = mon.musician ?? mon.mix;
    const label = who ? `${MONITOR_LABEL[mon.type]} ${who.trim()}` : MONITOR_LABEL[mon.type];
    const target = mon.mix === undefined ? undefined : mixFor(mixes.targets, mon.mix);
    for (let i = 0; i < (mon.quantity ?? 1); i++) {
      const key = uniqueKey(ctx, `monitor:${normalizeName(label)}`);
      let item: Item | undefined = claimExisting(ctx, [kind], label, target?.mix.outputItemIds);
      if (item !== undefined) {
        matchExisting(ctx, 'item', key, item, 'monitors', mon.position);
      } else {
        const p0 = place(ctx, mon.position);
        const musician = findMusician(ctx, mon.musician) ?? nearestMusician(ctx, p0);
        item = catalogItem(ctx, [kind], mon.model, kind, {
          id: ctx.nextId(),
          label,
          ...placeFor(ctx, mon.position, musician),
        });
        createItem(ctx, key, 'monitors', item, label, describe(mon.model, mon.mix));
      }
      if (target !== undefined && !target.mix.outputItemIds.includes(item.id)) {
        target.mix.outputItemIds.push(item.id);
      }
    }
  }
}

/** Channel a send names: one already in the mix first, then exact, then similar names. */
function channelForSend(ctx: Ctx, mix: Mix, ref: string): ChannelState | undefined {
  const inMix = (s: ChannelState) => mix.sends.some((x) => x.channelId === s.entity.id);
  const ordered = [...ctx.channels].sort(
    (a, b) => Number(inMix(b)) - Number(inMix(a)) || a.entity.number - b.entity.number,
  );
  return (
    ordered.find((s) => normalizeName(s.entity.name) === normalizeName(ref)) ??
    ordered.find((s) => similarName(s.entity.name, ref))
  );
}

function emitMixOps(ctx: Ctx, extraction: AiExtraction, mixes: MixPlan): void {
  extraction.mixes.forEach((w, i) => {
    const target = mixes.ext[i]!;
    for (const ref of w.sends ?? []) {
      const ch =
        channelByNumber(ctx, /^\d+$/.test(ref.trim()) ? Number(ref) : undefined) ??
        channelForSend(ctx, target.mix, ref);
      if (ch !== undefined && !target.mix.sends.some((s) => s.channelId === ch.entity.id)) {
        target.mix.sends.push({ channelId: ch.entity.id });
      }
    }
  });
  for (const t of mixes.targets) {
    const o = t.original;
    if (
      o !== undefined &&
      o.sends.length === t.mix.sends.length &&
      o.outputItemIds.length === t.mix.outputItemIds.length
    ) {
      continue;
    }
    ctx.ops.push({
      key: t.key,
      group: 'mixes',
      kind: t.isNew ? 'create' : 'patch',
      collection: 'mixes',
      entity: t.mix,
      label: `${t.mix.number} ${t.mix.name}`,
      detail: describe(
        t.mix.kind,
        t.mix.outputItemIds.length > 0 ? `${t.mix.outputItemIds.length}×` : undefined,
      ),
      defaultSelected: true,
    });
  }
}

function planStageboxes(ctx: Ctx, extraction: AiExtraction): void {
  for (const sb of extraction.stageboxes) {
    const label = sb.name.trim() || sb.model?.trim() || 'Stagebox';
    const key = uniqueKey(ctx, `stagebox:${normalizeName(label)}`);
    const hit = claimExisting(ctx, ['stagebox', 'subcore'], label);
    if (hit !== undefined) {
      matchExisting(ctx, 'item', key, hit, 'stageboxes', sb.position);
      continue;
    }
    const item = catalogItem(ctx, ['stagebox', 'subcore'], sb.model, 'stagebox', {
      id: ctx.nextId(),
      label,
      ...place(ctx, sb.position),
    });
    createItem(ctx, key, 'stageboxes', item, label, sb.model);
  }
}

function metres(v: number): string {
  return String(Number(v.toFixed(2)));
}

function planRisers(ctx: Ctx, extraction: AiExtraction): void {
  for (const r of extraction.risers) {
    const dims = [r.w, r.d, r.h].filter((v) => v !== undefined).map(metres);
    const label = r.label?.trim() || (dims.length > 0 ? `Riser ${dims.join(' × ')} m` : 'Riser');
    const key = uniqueKey(ctx, `riser:${normalizeName(label)}`);
    const hit = claimExisting(ctx, ['riser'], label);
    if (hit !== undefined) {
      matchExisting(ctx, 'item', key, hit, 'risers', r.position);
      continue;
    }
    const entry = ctx.catalog.find((e) => e.kind === 'riser' && e.width === r.w && e.depth === r.d);
    const base: Partial<Item> = {
      id: ctx.nextId(),
      label,
      ...place(ctx, r.position),
      ...(r.w !== undefined ? { width: r.w } : {}),
      ...(r.d !== undefined ? { depth: r.d } : {}),
      ...(r.h !== undefined ? { props: { height: r.h } } : {}),
    };
    const item = entry === undefined ? makeItem('riser', base) : itemFromCatalog(entry, base);
    createItem(ctx, key, 'risers', item, label, dims.join(' × '));
  }
}

// ---------------------------------------------------------------------------------------------
// Power

/** Plug of a power drop text ("Schuko", "CEE 16A", "CEE32 3-phase"); default by voltage. */
export function plugFromText(text: string | undefined, voltage: number): ConnectorType {
  const t = normalizeName(text ?? '');
  if (t.includes('cee32') || t.includes('32a')) return 'cee32';
  if (t.includes('cee')) return 'cee16';
  if (t.includes('powercon')) return 'powercon';
  if (t.includes('nema') || t.includes('edison')) return 'nema5_15';
  if (t.includes('schuko')) return 'schuko';
  return voltage <= 127 ? 'nema5_15' : 'schuko';
}

const PLUG_LABEL: Partial<Record<ConnectorType, string>> = {
  schuko: 'Schuko',
  cee16: 'CEE16',
  cee32: 'CEE32',
  powercon: 'powerCON',
  nema5_15: 'NEMA 5-15',
};

const DISTRO_FOR: Partial<Record<ConnectorType, string>> = {
  schuko: 'schuko',
  cee16: 'cee16',
  cee32: 'cee32',
  nema5_15: 'usstrip',
};

function planPower(ctx: Ctx, extraction: AiExtraction): void {
  const names = new Map<string, number>();
  for (const p of extraction.power) {
    const voltage = p.voltage ?? 230;
    const plug = plugFromText(p.plug, voltage);
    const base = describe(
      `${p.count !== undefined ? `${p.count}× ` : ''}${metres(voltage)} V`,
      PLUG_LABEL[plug],
    );
    const n = (names.get(base) ?? 0) + 1;
    names.set(base, n);
    const name = n === 1 ? base : `${base} (${n})`;
    const key = `power:${normalizeName(name)}`;
    const existing = Object.values(ctx.board.power).find(
      (s) => normalizeName(s.name) === normalizeName(name),
    );
    if (existing !== undefined) {
      ctx.matches.push({ kind: 'power', key, entityId: existing.id, label: existing.name });
      continue;
    }
    const source: PowerSource = {
      id: ctx.nextId(),
      name,
      voltage,
      frequency: voltage <= 127 ? 60 : 50,
      plug,
    };
    const placement = place(ctx, p.position);
    if (placement.unplaced !== true) {
      const alias = DISTRO_FOR[plug];
      const entries = ctx.catalog.filter((e) => e.kind === 'power_distro');
      const entry = alias === undefined ? undefined : matchCatalogToken(entries, alias);
      const fields: Partial<Item> = { id: ctx.nextId(), label: name, ...placement };
      if (p.notes) fields.notes = p.notes;
      const distro =
        entry === undefined ? makeItem('power_distro', fields) : itemFromCatalog(entry, fields);
      createItem(ctx, `${key}:distro`, 'power', distro, name, p.notes);
      source.itemId = distro.id;
    }
    ctx.ops.push({
      key,
      group: 'power',
      kind: 'create',
      collection: 'power',
      entity: source,
      label: name,
      ...(p.notes ? { detail: p.notes } : {}),
      defaultSelected: true,
    });
  }
}

// ---------------------------------------------------------------------------------------------
// Notes

function planNotes(ctx: Ctx, extraction: AiExtraction): void {
  const existing = Object.values(ctx.board.freeform);
  const known = new Set(existing.map((e) => normalizeName(e.text ?? '')));
  const f = ctx.frame;
  const x = f === undefined ? 0 : f.x + f.width + 0.5;
  let y = existing
    .filter((e) => e.x >= x - 0.01)
    .reduce((max, e) => Math.max(max, e.y + e.h + NOTE_GAP), f?.y ?? 0);
  for (const raw of extraction.notes) {
    const text = raw.trim();
    const n = normalizeName(text);
    if (n === '' || known.has(n)) continue;
    known.add(n);
    const note: FreeformElement = {
      id: ctx.nextId(),
      kind: 'sticky',
      x,
      y,
      w: NOTE_W,
      h: NOTE_H,
      rotation: 0,
      text,
      color: '#ffe58a',
    };
    y += NOTE_H + NOTE_GAP;
    ctx.ops.push({
      key: uniqueKey(ctx, `note:${n.slice(0, 40)}`),
      group: 'notes',
      kind: 'create',
      collection: 'freeform',
      entity: note,
      label: text.length > 80 ? `${text.slice(0, 79)}…` : text,
      defaultSelected: true,
    });
  }
}

// ---------------------------------------------------------------------------------------------

/**
 * Plans how an extraction lands on a board: channels by number (else name), musicians by name,
 * mics/DIs linked to channels (number, else purpose ≈ channel name) and musicians (name, else
 * nearest), catalog models, stands via `parseStandText`, positions inside the stage frame
 * (no position → unplaced tray), notes as sticky notes right of the stage. Existing values are
 * never overwritten by default – differing values become (unselected) conflict ops.
 */
export function planAiMerge(
  board: BoardData,
  extraction: AiExtraction,
  catalog: readonly CatalogEntry[],
  opts: AiMergeOptions = {},
): AiMergePlan {
  extraction = splitFurniture(extraction);
  const ctx: Ctx = {
    board,
    catalog,
    nextId: opts.newId ?? (() => newId()),
    frame: mainStage(board),
    ops: [],
    matches: [],
    conflicts: [],
    warnings: [...extraction.warnings],
    items: new Map(Object.entries(board.items)),
    claimed: new Set(),
    musicians: Object.values(board.items).filter((i) => i.kind === 'musician'),
    channels: [],
    byExtNumber: new Map(),
    sourced: new Set(),
    keys: new Map(),
    nearCount: new Map(),
  };
  planStage(ctx, extraction);
  planMusicians(ctx, extraction);
  planChannels(ctx, extraction);
  planSources(ctx, extraction);
  planInstruments(ctx, extraction);
  const mixes = planMixTargets(ctx, extraction);
  planMonitors(ctx, extraction, mixes);
  emitChannelOps(ctx);
  emitMixOps(ctx, extraction, mixes);
  planStageboxes(ctx, extraction);
  planRisers(ctx, extraction);
  planPower(ctx, extraction);
  planNotes(ctx, extraction);

  const byGroup: Partial<Record<AiOpGroup, number>> = {};
  for (const op of ctx.ops) byGroup[op.group] = (byGroup[op.group] ?? 0) + 1;
  return {
    ops: ctx.ops,
    matches: ctx.matches,
    conflicts: ctx.conflicts,
    warnings: ctx.warnings,
    summary: {
      creates: ctx.ops.filter((o) => o.kind === 'create').length,
      patches: ctx.ops.filter((o) => o.kind === 'patch').length,
      matches: ctx.matches.length,
      conflicts: ctx.conflicts.length,
      byGroup,
    },
  };
}

/** Whether an op is selected: the stored decision, else its default. */
export function isOpSelected(op: AiMergeOp, decisions: Readonly<Record<string, boolean>>): boolean {
  return decisions[op.key] ?? op.defaultSelected;
}

export interface AppliedAiMerge {
  /** The board after the selected ops. */
  data: BoardData;
  /** Entity writes in order (references to unselected creates removed, items clamped into the frame). */
  writes: AiMergeOp[];
  /** Ids of items created (for highlighting). */
  createdItemIds: string[];
}

function sanitize(data: BoardData, op: AiMergeOp): AiMergeOp {
  const has = (id: string | undefined) => id !== undefined && data.items[id] !== undefined;
  switch (op.collection) {
    case 'items': {
      const item = { ...op.entity };
      if (item.mountedOn !== undefined && !has(item.mountedOn)) delete item.mountedOn;
      if (item.frameId !== null) {
        const frame = data.frames[item.frameId];
        if (frame === undefined) Object.assign(item, IN_TRAY);
        else Object.assign(item, clampIntoFrame(frame, item.x, item.y));
      }
      return { ...op, entity: item };
    }
    case 'channels': {
      const ch = { ...op.entity };
      if (ch.sourceItemId !== undefined && !has(ch.sourceItemId)) {
        delete ch.sourceItemId;
        delete ch.sourcePortId;
      }
      return { ...op, entity: ch };
    }
    case 'mixes':
      return {
        ...op,
        entity: {
          ...op.entity,
          sends: op.entity.sends.filter((s) => data.channels[s.channelId] !== undefined),
          outputItemIds: op.entity.outputItemIds.filter((id) => has(id)),
        },
      };
    case 'power': {
      const source = { ...op.entity };
      if (source.itemId !== undefined && !has(source.itemId)) delete source.itemId;
      return { ...op, entity: source };
    }
    default:
      return op;
  }
}

function put(data: BoardData, op: AiMergeOp): void {
  (data[op.collection] as Record<string, unknown>)[op.entity.id] = op.entity;
}

/**
 * Applies the selected ops of a plan (pure). References to entities of unselected ops are
 * removed and placed items are clamped into the (possibly resized) frame, so the result never
 * dangles. The editor writes `writes` into the document in one transaction (one undo step).
 */
export function applyAiMerge(
  board: BoardData,
  plan: AiMergePlan,
  decisions: Readonly<Record<string, boolean>> = {},
): AppliedAiMerge {
  const data: BoardData = {
    ...board,
    frames: { ...board.frames },
    items: { ...board.items },
    channels: { ...board.channels },
    mixes: { ...board.mixes },
    power: { ...board.power },
    freeform: { ...board.freeform },
  };
  const selected = plan.ops.filter((op) => isOpSelected(op, decisions));
  const ordered = [
    ...selected.filter((op) => op.collection === 'frames'),
    ...selected.filter((op) => op.collection === 'items'),
    ...selected.filter((op) => op.collection === 'channels'),
    ...selected.filter(
      (op) =>
        op.collection !== 'frames' && op.collection !== 'items' && op.collection !== 'channels',
    ),
  ];
  for (const op of ordered) put(data, op);
  const writes = ordered.map((op) => sanitize(data, op));
  for (const op of writes) put(data, op);
  return {
    data,
    writes,
    createdItemIds: writes
      .filter((op) => op.collection === 'items' && op.kind === 'create')
      .map((op) => op.entity.id),
  };
}
